import { useState, useRef } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';

export interface DestructiveConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  /** When provided, the user must type this exact string to enable Confirm. */
  typeToConfirm?: string;
  /** When true, Confirm requires click-and-hold for 1 second. */
  holdToConfirm?: boolean;
  confirmLabel?: string;
}

export function DestructiveConfirm({
  open, onClose, onConfirm, title, description, typeToConfirm, holdToConfirm, confirmLabel = 'Delete',
}: DestructiveConfirmProps) {
  const [typed, setTyped] = useState('');
  const holdStart = useRef<number | null>(null);
  const [holdPct, setHoldPct] = useState(0);
  const holdTimer = useRef<number | null>(null);

  const canConfirm = !typeToConfirm || typed === typeToConfirm;

  const startHold = () => {
    holdStart.current = Date.now();
    holdTimer.current = window.setInterval(() => {
      const elapsed = Date.now() - (holdStart.current ?? 0);
      const pct = Math.min(100, (elapsed / 1000) * 100);
      setHoldPct(pct);
      if (pct >= 100) {
        stopHold();
        onConfirm();
        onClose();
      }
    }, 30);
  };
  const stopHold = () => {
    if (holdTimer.current) window.clearInterval(holdTimer.current);
    holdTimer.current = null;
    setHoldPct(0);
  };

  return (
    <Modal open={open} onClose={onClose} width={460} dismissible={!!typeToConfirm}>
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>{description}</p>
        {typeToConfirm && (
          <>
            <p style={{ fontSize: 13, marginBottom: 8 }}>Type <code style={{ fontFamily: 'JetBrains Mono, monospace', background: 'var(--bg-muted)', padding: '1px 6px', borderRadius: 4 }}>{typeToConfirm}</code> to confirm.</p>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {holdToConfirm ? (
          <button
            className="btn btn-destructive"
            data-testid="sweep-skip"
            onMouseDown={startHold}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            disabled={!canConfirm}
            style={{
              position: 'relative',
              overflow: 'hidden',
              padding: '0 12px', height: 32, borderRadius: 6, fontSize: 13, fontWeight: 500,
              background: 'var(--danger)', color: '#fff', border: 0, cursor: 'pointer',
              opacity: canConfirm ? 1 : 0.5,
            }}
          >
            {holdPct > 0 ? `Hold to ${confirmLabel.toLowerCase()}…` : `${confirmLabel} (hold)`}
            <span style={{ position: 'absolute', left: 0, bottom: 0, height: 2, width: `${holdPct}%`, background: '#fff' }} />
          </button>
        ) : (
          <Button variant="destructive" data-testid="sweep-skip" disabled={!canConfirm} onClick={() => { onConfirm(); onClose(); }}>
            {confirmLabel}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
